import { useEffect, useRef } from 'react';
import { useMutation, UseMutationResult } from 'react-query';
import { useTranslation } from 'react-i18next';
import { toast as uiToast } from '@kontentino/ui';
import { ApiClientError } from 'api/client';
import { PostExportVariant } from 'constants/post';
import { useToast } from 'app/hooks/useToast';
import {
  CreatePostExportParams,
  PostExport,
  PostExportFailureReason,
  PostsApi,
} from 'app/modules/posts/api/posts';
import {
  isAbortError,
  PollTimeoutError,
  pollUntilSettled,
  triggerDownload,
} from 'app/utils/asyncJob';

const TOAST_ID = 'posts-export';
const READY_TOAST_DURATION_MS = 15_000;

type ToastType = 'success' | 'warning' | 'error';

type FailureMessage = {
  key: string;
  type: ToastType;
  options?: { limit: number | null };
};

export function isApiExportVariant(
  variant: PostExportVariant,
  isMediaLibraryEnabled: boolean,
): variant is PostExportVariant.Pdf | PostExportVariant.Images {
  return (
    isMediaLibraryEnabled &&
    (variant === PostExportVariant.Pdf || variant === PostExportVariant.Images)
  );
}

class PostExportFailedError extends Error {
  constructor(
    public readonly reason: PostExportFailureReason | null,
    public readonly limit: number | null,
  ) {
    super(reason ?? 'failed');
    this.name = 'PostExportFailedError';
  }
}

function isPending(postExport: PostExport): boolean {
  return postExport.status === 'pending' || postExport.status === 'processing';
}

function isInflightLimit(error: unknown): boolean {
  if (!(error instanceof ApiClientError)) return false;
  const body = error.axiosError?.response?.data as
    | { errors?: Record<string, string[]> }
    | undefined;
  return body?.errors?.export?.[0] === 'POST_EXPORT_INFLIGHT_LIMIT';
}

function describeFailure(error: unknown): FailureMessage {
  if (error instanceof PollTimeoutError) {
    return { key: 'postsExport.tooSlow', type: 'error' };
  }
  if (isInflightLimit(error)) {
    return { key: 'postsExport.inflightLimit', type: 'error' };
  }
  if (error instanceof PostExportFailedError) {
    switch (error.reason) {
      case 'no_posts':
        return { key: 'postsExport.noPosts', type: 'warning' };
      case 'no_media':
        return { key: 'postsExport.noMedia', type: 'warning' };
      case 'too_many_posts':
        return {
          key: 'postsExport.tooManyPosts',
          type: 'error',
          options: { limit: error.limit },
        };
      case 'too_many_files':
        return {
          key: 'postsExport.tooManyFiles',
          type: 'error',
          options: { limit: error.limit },
        };
      case 'timeout':
        return { key: 'postsExport.tooSlow', type: 'error' };
      default:
        return { key: 'postsExport.failed', type: 'error' };
    }
  }
  return { key: 'postsExport.failed', type: 'error' };
}

export default function usePostsExport(): UseMutationResult<
  PostExport,
  unknown,
  CreatePostExportParams
> {
  const { t } = useTranslation();
  const toast = useToast();
  const controllerRef = useRef<AbortController | null>(null);

  useEffect(
    () => () => {
      if (!controllerRef.current) return;
      controllerRef.current.abort();
      uiToast.dismiss(TOAST_ID);
    },
    [],
  );

  const showMessage = (
    text: string,
    type: ToastType | 'default',
    duration?: number,
  ) =>
    toast(<span data-cy="posts-export_toast">{text}</span>, type, {
      id: TOAST_ID,
      // omit when undefined: an explicit `undefined` would override useToast's
      // own error/warning autoclose
      ...(duration !== undefined && { duration }),
    });

  return useMutation<PostExport, unknown, CreatePostExportParams>(
    async (params) => {
      const controller = new AbortController();
      controllerRef.current = controller;
      const { signal } = controller;

      showMessage(
        t(
          params.format === 'pdf'
            ? 'postsExport.preparingPdf'
            : 'postsExport.preparingImages',
        ),
        'default',
        Infinity,
      );

      try {
        const created = await PostsApi.createExport(params);
        const settled = await pollUntilSettled({
          initial: created,
          isPending,
          fetch: () => PostsApi.getExport(created.id),
          signal,
        });

        if (settled.status === 'failed' || !settled.download_url) {
          throw new PostExportFailedError(
            settled.failure_reason,
            settled.limit,
          );
        }
        if (signal.aborted) throw new DOMException('aborted', 'AbortError');

        triggerDownload(settled.download_url);
        const isPartial = (settled.missing_file_count ?? 0) > 0;
        toast(
          <span data-cy="posts-export_toast">
            {t(
              settled.format === 'pdf'
                ? 'postsExport.readyPdf'
                : 'postsExport.readyImages',
            )}{' '}
            {isPartial && `${t('postsExport.partial')} `}
            <a
              data-cy="posts-export_download-link"
              href={settled.download_url}
              rel="noopener noreferrer"
            >
              {t('postsExport.downloadLink')}
            </a>
          </span>,
          isPartial ? 'warning' : 'success',
          { id: TOAST_ID, duration: READY_TOAST_DURATION_MS },
        );
        return settled;
      } catch (error) {
        if (signal.aborted || isAbortError(error)) throw error;
        const { key, type, options } = describeFailure(error);
        showMessage(options ? t(key, options) : t(key), type);
        throw error;
      } finally {
        controller.abort();
        if (controllerRef.current === controller) controllerRef.current = null;
      }
    },
  );
}
